import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardHeader, CardContent, LinearProgress, Alert,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, InputLabel, FormControl, Grid, Typography, Stack,
  Divider, Tabs, Tab, Badge, Avatar, Pagination
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useToast } from '../../hooks/useToast';

const CONDITION_COLOR = { like_new: 'success', used: 'warning', fair: 'default', excellent: 'primary', good: 'info' };
const STATUS_2H_COLOR = { active: 'success', pending: 'warning', approved: 'primary', rejected: 'error', closed: 'default', canceled: 'default' };
const CONDITION_LABEL = { like_new: 'Like New', used: 'Used', fair: 'Fair', excellent: 'Excellent', good: 'Good' };

const AdminSecondHand = ({
  secondHand,
  secondHandLoading,
  secondHandError,
  secondHandPage,
  secondHandTotalPages,
  secondHandSearch,
  setSecondHandSearch,
  showSecondHandModal,
  editingSecondHand,
  secondHandForm,
  secondHandFormError,
  secondHandFormLoading,
  showBidValidationModal,
  selectedSecondHand,
  openSecondHandModal,
  closeSecondHandModal,
  handleSecondHandFormSubmit,
  handleSecondHandFormChange,
  handleDeleteSecondHand,
  openBidValidationModal,
  closeBidValidationModal,
  handleApproveBid,
  fetchSecondHand
}) => {
  const toast = useToast();

  // State for auction room requests
  const [activeTab, setActiveTab] = useState('auctions'); // 'auctions' or 'requests'
  const [auctionRequests, setAuctionRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(null);
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  // Fetch auction room requests
  const fetchAuctionRequests = async () => {
    try {
      setRequestsLoading(true);
      setRequestsError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-room-requests/admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch auction requests');
      }

      const data = await response.json();
      if (data.success) {
        setAuctionRequests(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch auction requests');
      }
    } catch (error) {
      console.error('Error fetching auction requests:', error);
      setRequestsError(error.message);
    } finally {
      setRequestsLoading(false);
    }
  };

  // Review auction request (approve/reject)
  const handleReviewRequest = async (requestId, action, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-room-requests/${requestId}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action,
          reviewNotes: notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to review request');
      }

      const data = await response.json();
      if (data.success) {
        toast.success(`Request ${action}d successfully!`);
        fetchAuctionRequests(); // Refresh the list
        setReviewingRequest(null);
        setReviewAction('');
        setReviewNotes('');
      } else {
        throw new Error(data.message || 'Failed to review request');
      }
    } catch (error) {
      console.error('Error reviewing request:', error);
      toast.error(error.message);
    }
  };

  // Load auction requests when tab changes
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchAuctionRequests();
    }
  }, [activeTab]);

  const pendingCount = auctionRequests.filter(r => r.status === 'pending').length;

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
      <CardHeader
        title="Second-Hand Auctions"
        subheader="Manage second-hand listings and seller auction requests"
        titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
      />
      <Divider />
      <Box sx={{ px: 3, pt: 1 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Active Auctions" value="auctions" />
          <Tab
            label={
              <Badge badgeContent={pendingCount} color="error">
                <Box sx={{ pr: pendingCount > 0 ? 1.5 : 0 }}>Auction Requests</Box>
              </Badge>
            }
            value="requests"
          />
        </Tabs>
      </Box>

      {/* Active Auctions Tab */}
      {activeTab === 'auctions' && (
        <>
          {secondHandLoading && <LinearProgress color="primary" />}
          <CardContent sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search second-hand auctions…"
                value={secondHandSearch}
                onChange={e => setSecondHandSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: '100%', flex: 1 }}
              />
              <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => openSecondHandModal()} sx={{ borderRadius: 2 }}>
                Add Auction
              </Button>
            </Stack>
            {secondHandError && <Alert severity="error" sx={{ mb: 2 }}>{secondHandError}</Alert>}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Reserve Price</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Seller</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {secondHand.map(item => (
                    <TableRow key={item._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{item.category}</TableCell>
                      <TableCell>
                        <Chip label={CONDITION_LABEL[item.condition] || item.condition} size="small" color={CONDITION_COLOR[item.condition] || 'default'} />
                      </TableCell>
                      <TableCell>${item.reservePrice?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>{item.duration} days</TableCell>
                      <TableCell>
                        <Chip label={item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Unknown'} size="small" color={STATUS_2H_COLOR[item.status] || 'default'} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {item.sellerId?.username || item.seller?.username || 'Unknown'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => openSecondHandModal(item)} title="Edit">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteSecondHand(item)} title="Delete">
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                        {item.status === 'pending' && (
                          <IconButton size="small" color="success" onClick={() => openBidValidationModal(item)} title="Approve">
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!secondHandLoading && secondHand.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        <Stack alignItems="center" spacing={1}>
                          <HandshakeOutlinedIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                          <Typography variant="body2">No second-hand auctions found</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {secondHandTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={secondHandTotalPages}
                  page={secondHandPage}
                  onChange={(_, p) => fetchSecondHand(p, secondHandSearch)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </CardContent>
        </>
      )}

      {/* Auction Requests Tab */}
      {activeTab === 'requests' && (
        <>
          {requestsLoading && <LinearProgress color="primary" />}
          <CardContent sx={{ pt: 1 }}>
            {requestsError && (
              <Alert severity="error" action={<Button size="small" onClick={fetchAuctionRequests}><RefreshOutlinedIcon fontSize="small" /></Button>} sx={{ mb: 2 }}>
                {requestsError}
              </Alert>
            )}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                    <TableCell>Product</TableCell>
                    <TableCell>Seller</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Starting Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auctionRequests.map(request => (
                    <TableRow key={request._id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {request.images?.[0]?.thumbnailUrl && (
                            <Avatar src={request.images[0].thumbnailUrl} alt={request.productName} variant="rounded" sx={{ width: 36, height: 36 }} />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{request.productName}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {request.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{request.sellerId?.username || 'Unknown'}</Typography>
                        <Typography variant="caption" color="text.secondary">{request.sellerId?.email || ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{request.category}</TableCell>
                      <TableCell>
                        <Chip label={CONDITION_LABEL[request.condition] || request.condition?.replace('_', ' ')} size="small" color={CONDITION_COLOR[request.condition] || 'default'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">€{request.startingPrice?.toFixed(2) || '0.00'}</Typography>
                        {request.reservePrice > 0 && (
                          <Typography variant="caption" color="text.secondary">Reserve: €{request.reservePrice.toFixed(2)}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1) : 'Unknown'} size="small" color={STATUS_2H_COLOR[request.status] || 'default'} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {new Date(request.submittedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        {request.status === 'pending' ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <IconButton size="small" color="success" onClick={() => { setReviewingRequest(request); setReviewAction('approve'); }} title="Approve">
                              <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => { setReviewingRequest(request); setReviewAction('reject'); }} title="Reject">
                              <CancelOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Stack alignItems="flex-end">
                            <Typography variant="caption" color={request.status === 'approved' ? 'success.main' : 'error.main'}>
                              {request.status === 'approved' ? 'âœ“ Approved' : 'âœ— Rejected'}
                            </Typography>
                            {request.reviewedAt && (
                              <Typography variant="caption" color="text.secondary">
                                {new Date(request.reviewedAt).toLocaleDateString()}
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!requestsLoading && auctionRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        <Typography variant="body2">No auction requests found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewingRequest} onClose={() => { setReviewingRequest(null); setReviewAction(''); setReviewNotes(''); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {reviewAction === 'approve' ? 'Approve Request' : 'Reject Request'}
          <IconButton size="small" onClick={() => { setReviewingRequest(null); setReviewAction(''); setReviewNotes(''); }}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {reviewingRequest && (
            <Stack spacing={2}>
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>{reviewingRequest.productName}</Typography>
                <Typography variant="body2" color="text.secondary">{reviewingRequest.description}</Typography>
                <Typography variant="caption" color="text.secondary">Seller: {reviewingRequest.sellerId?.username}</Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                label={`Review Notes${reviewAction === 'reject' ? ' (Required)' : ' (Optional)'}`}
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                placeholder={reviewAction === 'approve' ? 'Optional notes for approval…' : 'Please provide a reason for rejection…'}
                required={reviewAction === 'reject'}
              />
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setReviewingRequest(null); setReviewAction(''); setReviewNotes(''); }} variant="outlined" color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color={reviewAction === 'approve' ? 'success' : 'error'}
            onClick={() => {
              if (reviewAction === 'reject' && !reviewNotes.trim()) { toast.warning('Please provide a reason for rejection'); return; }
              handleReviewRequest(reviewingRequest._id, reviewAction, reviewNotes);
            }}
          >
            {reviewAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Add Second-Hand Dialog */}
      <Dialog open={showSecondHandModal} onClose={closeSecondHandModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HandshakeOutlinedIcon />
            {editingSecondHand ? 'Edit Second-Hand Auction' : 'Add Second-Hand Auction'}
          </Box>
          <IconButton onClick={closeSecondHandModal} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleSecondHandFormSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Name *" name="name" value={secondHandForm.name} onChange={handleSecondHandFormChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Category *" name="category" value={secondHandForm.category} onChange={handleSecondHandFormChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Condition *</InputLabel>
                    <Select name="condition" value={secondHandForm.condition} label="Condition *" onChange={handleSecondHandFormChange}>
                      <MenuItem value=""><em>Select</em></MenuItem>
                      <MenuItem value="excellent">Excellent</MenuItem>
                      <MenuItem value="good">Good</MenuItem>
                      <MenuItem value="like_new">Like New</MenuItem>
                      <MenuItem value="used">Used</MenuItem>
                      <MenuItem value="fair">Fair</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Reserve Price *" name="reservePrice" value={secondHandForm.reservePrice} onChange={handleSecondHandFormChange} required inputProps={{ min: 0, step: 0.01 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Duration (days) *" name="duration" value={secondHandForm.duration} onChange={handleSecondHandFormChange} required inputProps={{ min: 1, max: 30 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select name="status" value={secondHandForm.status} label="Status" onChange={handleSecondHandFormChange}>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                      <MenuItem value="canceled">Canceled</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <TextField fullWidth size="small" multiline rows={3} label="Description *" name="description" value={secondHandForm.description} onChange={handleSecondHandFormChange} required />
              {secondHandFormError && <Alert severity="error">{secondHandFormError}</Alert>}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeSecondHandModal} variant="outlined" color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={secondHandFormLoading}>
              {secondHandFormLoading ? 'Saving…' : (editingSecondHand ? 'Update Item' : 'Add Item')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Bid Validation (Approve) Dialog */}
      <Dialog open={showBidValidationModal} onClose={closeBidValidationModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Approve Second-Hand Auction</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>Are you sure you want to approve this auction?</Typography>
          {selectedSecondHand && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>{selectedSecondHand.name}</Typography>
              <Typography variant="caption" display="block">Reserve Price: ${selectedSecondHand.reservePrice?.toFixed(2) || '0.00'}</Typography>
              <Typography variant="caption" display="block">Condition: {CONDITION_LABEL[selectedSecondHand.condition] || selectedSecondHand.condition}</Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeBidValidationModal} variant="outlined" color="inherit">Cancel</Button>
          <Button variant="contained" color="success" onClick={() => { handleApproveBid(selectedSecondHand); closeBidValidationModal(); }}>
            Approve Auction
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AdminSecondHand;
